import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { Button, Icon } from '@unite-us/app-components';

const ACTION_CONFIG = {
  complete: {
    title: 'Mark Tasks as Complete',
    confirmLabel: 'Mark as Complete',
    confirmTestId: 'confirm-mark-complete-tasks',
    body: (count) => `Are you sure you want to mark ${count} ${count === 1 ? 'task' : 'tasks'} as complete?`,
  },
  archived: {
    title: 'Archive Tasks',
    confirmLabel: 'Archive',
    confirmTestId: 'confirm-archive-tasks',
    body: (count) => `Are you sure you want to archive ${count} ${count === 1 ? 'task' : 'tasks'}?`,
  },
};

const BulkStatusUpdateModal = ({
  show,
  onClose,
  onConfirm,
  loading,
  statusAction,
  selectedCount,
}) => {
  const config = ACTION_CONFIG[statusAction] ?? ACTION_CONFIG.complete;

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: 110 }}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-0"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-0"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-0"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-0"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={
                  'relative transform rounded-lg bg-white text-left shadow-xl ' +
                  'transition-all sm:mb-8 sm:mt-20 sm:w-full sm:max-w-md'
                }
                data-testid="bulk-status-update-modal"
              >
                <Dialog.Title
                  as="div"
                  className="bg-white px-6 py-4 flex justify-between items-center rounded-t-lg"
                >
                  <h3 className="text-lg font-extrabold leading-6 text-dark-blue">
                    {config.title}
                  </h3>
                  <Icon
                    icon="Cross"
                    size={12}
                    color="dark-blue"
                    onClick={onClose}
                    className="stroke-current stroke-1 cursor-pointer"
                  />
                </Dialog.Title>

                <Dialog.Description
                  as="div"
                  className="px-6 py-5 bg-light-border-grey border-t border-b border-solid border-med-pale-blue"
                >
                  <p className="text-sm text-gray-700">
                    {config.body(selectedCount)}
                  </p>
                </Dialog.Description>

                <div className="gap-2 bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-lg">
                  <Button
                    label={loading ? 'Saving...' : config.confirmLabel}
                    data-testid={config.confirmTestId}
                    primary
                    disabled={loading}
                    onClick={onConfirm}
                    className="font-regular-font"
                  />
                  <Button
                    label="Cancel"
                    data-testid="cancel-bulk-status-update"
                    secondary
                    disabled={loading}
                    onClick={onClose}
                    className="font-regular-font"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

BulkStatusUpdateModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  statusAction: PropTypes.oneOf(['complete', 'archived']).isRequired,
  selectedCount: PropTypes.number.isRequired,
};

export default BulkStatusUpdateModal;
